import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight, Video } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import ServiceCard from "../components/ServiceCard";
import CTABand from "../components/CTABand";
import { SERVICES, INSURANCES } from "../data/site";
import { COUNTIES } from "../data/counties";
import { breadcrumbSchema, serviceSchema } from "../lib/schema";
import { unsplash, unsplashSrcSet } from "../lib/img";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const path = `/services/${service.slug}`;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: service.title, path },
  ];
  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        path={path}
        schema={[breadcrumbSchema(crumbs), serviceSchema({ name: service.h1, description: service.metaDescription, path })]}
      />
      <PageHero crumbs={crumbs} eyebrow={service.subtitle} title={service.h1.replace(" in New Jersey", " in")} accent="New Jersey" underline body={service.intro} testid="service-hero">
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Link to={`/contact?service=${service.slug}`} className="btn btn-primary" data-testid="service-cta">
            Book a Session <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </PageHero>

      <section className="section" data-testid="service-body">
        <div className="container-site grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-12">
            <div>
              <h2 className="text-2xl md:text-3xl">What we help with</h2>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="service-helps">
                {service.helps.map((h) => (
                  <li key={h} className="flex gap-3 text-[15px] text-[var(--ink)]">
                    <Check className="h-5 w-5 text-[var(--sage)] shrink-0" /> {h}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl">Our approach</h2>
              <div className="mt-6 flex flex-wrap gap-2">
                {service.approach.map((a) => (
                  <span key={a} className="chip">{a}</span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl bg-[var(--sage-tint)] p-6 flex gap-4">
              <Video className="h-5 w-5 text-[var(--sage)] shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base">How it works online</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-body">{service.virtualNote}</p>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-5">
            <div className="overflow-hidden rounded-2xl aspect-[4/3] bg-[var(--sage-tint)]">
              <img src={unsplash(service.image, 800)} srcSet={unsplashSrcSet(service.image, [480, 800, 1100])} sizes="(min-width: 1024px) 440px, 100vw" width="800" height="600" alt={`${service.title} online in New Jersey`} className="h-full w-full object-cover transition-transform [transition-duration:1200ms] hover:scale-105" loading="lazy" decoding="async" />
            </div>
            <div className="card p-6">
              <h3 className="text-base">Insurance accepted</h3>
              <p className="mt-2 text-sm text-body leading-relaxed">{INSURANCES.join(" · ")}</p>
            </div>
            <div className="card p-6">
              <h3 className="text-base">Available statewide</h3>
              <p className="mt-2 text-sm text-body leading-relaxed">
                {service.title} is offered online to clients in every New Jersey county, including{" "}
                {["bergen-county", "morris-county", "monmouth-county", "middlesex-county", "camden-county"].map((s, i, arr) => {
                  const c = COUNTIES.find((x) => x.slug === s);
                  return (
                    <span key={s}>
                      <Link to={`/areas-we-serve/${s}`} className="text-[var(--sage)] underline underline-offset-2">{c.name}</Link>
                      {i < arr.length - 1 ? ", " : ""}
                    </span>
                  );
                })}{" "}
                and <Link to="/areas-we-serve" className="text-[var(--sage)] underline underline-offset-2">every other NJ county</Link>.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="section !pt-0" data-testid="service-related">
        <div className="container-site">
          <h2 className="text-2xl md:text-3xl">Other services</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      <CTABand title={`Start ${service.title.toLowerCase()} online`} to={`/contact?service=${service.slug}`} />
    </>
  );
}
